import type { ReactNode } from 'react';

import { EmptyArt } from './art/EmptyArt';
import { EyesArt } from './art/EyesArt';
import { cn } from '@/lib/cn';

/**
 * Lo stato vuoto di una sezione: un'illustrazione e una riga di testo.
 *
 * **Niente card di sfondo, di proposito.** Una superficie serve a proteggere la
 * lettura di un elenco; disegnata attorno a due righe di testo diventa una
 * cornice attorno al vuoto, e dice «manca qualcosa» invece di «non c'è niente»
 * — che nella maggior parte dei casi è uno stato normale, non un guasto. La
 * card torna quando torna il contenuto.
 *
 * L'illustrazione è `aria-hidden` con `alt=""`: non aggiunge nulla a chi legge
 * con uno screen reader, e annunciarla ruberebbe il posto al testo, che è la
 * parte che informa davvero.
 */
export function EmptyState({
  /** `empty` (default) o `eyes` per «niente da guardare» (Live). */
  art = 'empty',
  title,
  children,
  className,
}: {
  art?: 'empty' | 'eyes';
  title?: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex h-full flex-col items-center justify-center gap-4 px-6 py-8 text-center',
        className,
      )}
    >
      {/* I disegni sono DISEGNATI qui, non caricati come immagini: solo così
          possono prendere l'accento del tema (vedi `EmptyArt` / `EyesArt`).
          `text-brand` è ciò che li colora — dentro leggono `currentColor`.
          Entrambe le illustrazioni sono LARGHE e basse, quindi il limite che
          conta è la larghezza; il tetto in `vh` resta come rete sui pannelli
          molto bassi, dove il disegno spingerebbe fuori il testo — cioè l'unica
          parte che spiega perché la schermata è vuota. */}
      {art === 'eyes' ? (
        <EyesArt className="w-full max-w-[16rem] text-brand opacity-90" />
      ) : (
        <EmptyArt className="h-auto w-full max-h-[24vh] max-w-[11rem] text-brand opacity-90" />
      )}
      {title && <p className="text-base font-bold text-fg">{title}</p>}
      {children && <p className="max-w-[18rem] text-sm text-fg-muted">{children}</p>}
    </div>
  );
}
